import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Users } from "lucide-react";
import { migrationApi } from "../../services/migrationApi";
import { StatusBadge } from "../../components/StatusBadge";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

export default function MigrationReviewPage() {
  const { t } = useLocalization();
  const { canViewMigration } = usePermissions();
  const [profiles, setProfiles] = useState([]);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    if (!canViewMigration) return;
    migrationApi.listProfiles().then((r) => setProfiles(r.data)).catch(() => {});
  }, [canViewMigration]);

  if (!canViewMigration) {
    return <p className="text-sm text-gray-500" data-testid="migration-review-access-denied">{t("common.accessDenied")}</p>;
  }

  const filtered = filter === "ALL" ? profiles : profiles.filter((p) => p.profile_status === filter);
  const statuses = ["ALL", ...Array.from(new Set(profiles.map((p) => p.profile_status)))];

  return (
    <div data-testid="migration-review-page">
      <PageHelpBanner pageKey="migration-review" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtMigration.reviewHelp")}</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtMigration.reviewTitle")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtMigration.reviewSubtitle")}</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-full border border-gray-200 px-4 py-2" data-testid="migration-profile-count">
          <Users size={16} className="text-blue-600" />
          <span className="text-sm font-semibold text-gray-700">{profiles.length} {t("mgmtMigration.profiles")}</span>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {statuses.map((s) => (
          <button
            key={s}
            data-testid={`migration-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colProposedId")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colLegalName")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colPreferredName")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colReviewStatus")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colEmployment")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colScheduling")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colWage")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colAccount")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtMigration.colFlags")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" data-testid={`migration-row-${p.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/management/workforce/migration-review/${p.id}`} className="text-blue-600 font-semibold hover:underline" data-testid={`migration-detail-link-${p.id}`}>
                    {p.proposed_employee_id}
                  </Link>
                </td>
                <td className="px-4 py-3">{p.confirmed_legal_name || <span className="text-amber-600 italic">{p.legal_name_source} ({t("mgmtMigration.unconfirmed")})</span>}</td>
                <td className="px-4 py-3">{p.confirmed_preferred_name || p.preferred_name || "-"}</td>
                <td className="px-4 py-3"><StatusBadge status={p.profile_status} /></td>
                <td className="px-4 py-3"><StatusBadge status={p.employment_status} /></td>
                <td className="px-4 py-3"><StatusBadge status={p.scheduling_eligibility} /></td>
                <td className="px-4 py-3"><StatusBadge status={p.wage_configuration_status} /></td>
                <td className="px-4 py-3"><StatusBadge status={p.account_status} /></td>
                <td className="px-4 py-3">
                  {p.duplicate_status === "POSSIBLE_DUPLICATE" && (
                    <span className="flex items-center gap-1 text-xs text-amber-600 font-semibold" data-testid={`migration-duplicate-flag-${p.id}`}>
                      <AlertTriangle size={13} /> {t("mgmtMigration.duplicateQuestion")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-500">{t("mgmtMigration.noProfilesFilter")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
